import { useEffect, useState } from "react";
import clsx from "clsx";
import { themeParams, useSignal } from "@telegram-apps/sdk-react";
import { ActionType } from "~/pages/constants";
import { PairedToken, TradeToken } from "~/types";
import { buildTokenCurrecncyIcon } from "../utils";
import SvgRenderer from "~/components/SvgRenderer/SvgRenderer";
import useSuggestionAmounts from "../hooks/useSuggectionAmounts";
import { div, times } from "~/utils/calcNumber";
import eventBus from "~/tools/eventBus";

interface TokenAmountSelectorProps {
    tradeActionType: ActionType;
    tradeToken: TradeToken;
    pairedToken: PairedToken;
    availableSymbol: string | undefined;
    handleTopAmountChange: (value: string) => void;
    handleBottomAmountChange: (value: string) => void;
}

const TokenAmountSelector: React.FC<TokenAmountSelectorProps> = ({
    tradeActionType,
    tradeToken,
    pairedToken,
    availableSymbol,
    handleTopAmountChange,
    handleBottomAmountChange,
}) => {
    const isDark = useSignal(themeParams.isDark);
    const chainId = tradeToken?.token_base?.chain_id;
    const amounts = useSuggestionAmounts(
        String(chainId),
        availableSymbol ?? "",
        tradeActionType
    );
    const availableAmounts = amounts ?? [];

    const tokenCurrencyIcon = buildTokenCurrecncyIcon(
        availableSymbol ?? "",
        isDark
    );

    const [selectedIndex, setSelectedIndex] = useState<number>();

    useEffect(() => {
        setSelectedIndex(undefined);
    }, [
        tradeActionType,
        tradeToken?.token_base?.chain_id,
        tradeToken?.token_base?.contract,
        pairedToken?.token_base?.chain_id,
        pairedToken?.token_base?.contract,
        availableSymbol,
    ]);

    useEffect(() => {
        const onTopInputChange = () => {
            setSelectedIndex(undefined);
        };

        const onBottomInputChange = () => {
            setSelectedIndex(undefined);
        };

        eventBus.on("TradeFormTopInputChange", onTopInputChange);
        eventBus.on("TradeFormBottomInputChange", onBottomInputChange);

        return () => {
            eventBus.off("TradeFormTopInputChange", onTopInputChange);
            eventBus.off("TradeFormBottomInputChange", onBottomInputChange);
        };
    }, []);

    const onBuyAmountItemClicked = (amount: number) => {
        // @ts-ignore
        handleBottomAmountChange(String(amount), true);
    };

    const onSellPercentItemClicked = (percent: number) => {
        const newlyTopAmount =
            percent === 100
                ? tradeToken?.balance
                : div(times(Number(tradeToken?.balance), percent, 6), 100, 6);

        // @ts-ignore
        handleTopAmountChange(String(newlyTopAmount), true);
    };

    return (
        <div className="flex gap-0.5">
            {availableAmounts.map((amount, index) => {
                return (
                    <div
                        key={index}
                        className={clsx(
                            "flex-1 w-0 h-[30px] rounded-[4px] flex items-center px-1 text-body3 cursor-pointer",
                            index === selectedIndex
                                ? "bg-AlphaOrange outline outline-button_brand outline-1 text-text_brand font-semibold"
                                : "bg-bg_bottom text-text_2",
                            tradeActionType === ActionType.Sell &&
                                "justify-center"
                        )}
                        onClick={() => {
                            if (selectedIndex === index) {
                                setSelectedIndex(undefined);
                                if (tradeActionType === ActionType.Buy) {
                                    // @ts-ignore
                                    handleBottomAmountChange("", true);
                                } else {
                                    // @ts-ignore
                                    handleTopAmountChange("", true);
                                }
                            } else {
                                setSelectedIndex(index);
                                if (tradeActionType === ActionType.Buy) {
                                    onBuyAmountItemClicked(
                                        availableAmounts[index]
                                    );
                                } else {
                                    onSellPercentItemClicked(
                                        availableAmounts[index]
                                    );
                                }
                            }
                        }}
                    >
                        {tradeActionType === ActionType.Buy &&
                            (tokenCurrencyIcon ? (
                                <SvgRenderer
                                    url={tokenCurrencyIcon}
                                    className={"w-3.5 h-3.5 text-body3 mr-0.5"}
                                />
                            ) : (
                                <span className="w-3.5 h-3.5 text-body3 font-semibold mr-0.5 flex items-center">
                                    {availableSymbol?.trim().charAt(0)}
                                </span>
                            ))}
                        <span>
                            {amount}
                            {tradeActionType === ActionType.Sell && "%"}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export default TokenAmountSelector;
